import type { Angabe, Vormerkung } from "../design/Rueckgaengig";
import type { Umschlag } from "../plattform/api";
import { positionMessen, type Messung } from "../plattform/position";

/**
 * Meldungen der Lage im Feld (oberflaeche.md §5): Sie bekommen beim Erfassen ihre Meldungs-Id, stehen 10 Sekunden im
 * Rückgängig-Fenster und gehen danach in die Warteschlange (ADR-0020, ADR-0023). Die Position des Geräts wird beim
 * Erfassen gemessen und ergänzt die noch offene Meldung, sobald sie vorliegt; ohne Position wird trotzdem gemeldet
 * (LIM-0009). Titel in der Warteschlange nach §1.5.
 */
export interface Rueckgaengig {
  vormerken(v: Vormerkung): Umschlag;
  ergaenzen(meldungsId: string, daten: Record<string, unknown>, angaben?: readonly Angabe[]): boolean;
}

export const TITEL_STAND_ERREICHT = "Stand erreicht";

/** Meldung mit Position: die Angaben der Messung ergänzen die vorgemerkte Meldung, sobald das Gerät sie liefert. */
export function mitPosition(rg: Rueckgaengig, v: Vormerkung, felder: (m: Messung) => Record<string, unknown> = mitGenauigkeit,
  messung: Promise<Messung | null> = positionMessen()): Umschlag {
  const u = rg.vormerken(v);
  void messung.then((m) => {
    if (m) rg.ergaenzen(u.meldungsId, felder(m));
  });
  return u;
}

/** Position und Genauigkeit (befehle.md §6.4: Stand erreicht, Hund vermisst, Hund gesichtet, Wildsichtung). */
export function mitGenauigkeit(m: Messung): Record<string, unknown> {
  return { Position: m.position, Genauigkeit: m.genauigkeit };
}

/** Nur die Position (befehle.md §6.4: Treibergruppenmeldungen tragen keine Genauigkeit). */
export function nurPosition(m: Messung): Record<string, unknown> {
  return { Position: m.position };
}

export function standErreichtMelden(rg: Rueckgaengig, drueckjagdId: string, standId: string, standnummer: string | null): Umschlag {
  return mitPosition(rg, {
    befehl: "StandErreichtMelden", begriff: "Stand erreicht", symbol: "stand-erreicht", angaben: [["stand", standnummer ? `Stand ${standnummer}` : "Stand"]],
    titel: () => TITEL_STAND_ERREICHT, daten: { "Drückjagd": drueckjagdId, Stand: standId },
  });
}

export type Treibergruppenmeldung = "am Start" | "am Sammelpunkt";

export function treibergruppeMelden(rg: Rueckgaengig, drueckjagdId: string, revier: string, gruppe: string, meldung: Treibergruppenmeldung): Umschlag {
  return mitPosition(rg, {
    befehl: meldung === "am Start" ? "TreibergruppeAmStartMelden" : "TreibergruppeAmSammelpunktMelden",
    begriff: meldung, symbol: meldung === "am Start" ? "am-start" : "am-sammelpunkt", angaben: [["treiberfuehrer", `Treibergruppe ${gruppe}`]],
    titel: () => `Treibergruppe ${gruppe} ${meldung}`, daten: { "Drückjagd": drueckjagdId, Revier: revier, Treibergruppe: gruppe },
  }, nurPosition);
}
